import React, { useState, useMemo } from 'react';
import {
  Box,
  Heading,
  Divider,
  Flex,
  Stack,
  Button,
  useToast,
  VStack,
  Text,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Image,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { Select as ChakraSelect } from 'chakra-react-select';
import axiosInstance from '../../../services/axios';
import { useAuth } from '../../../hooks/useAuth';
import { QRCodeCanvas } from 'qrcode.react';
import Settings from './Settings'; // Import the Settings component
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { components } from 'react-select';
import { useNavigate } from 'react-router-dom';

const ArtistProfile = () => {
  const { control } = useForm();
  const toast = useToast();
  const auth = useAuth();
  const navigate = useNavigate();

  // User & Profile data - with safe defaults
  const artistData = auth.user?.account?.profile || {};

  // States for Profile Update
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isLoadingGenres, setIsLoadingGenres] = useState(false);
  const [isLoadingNames, setIsLoadingNames] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(artistData?.image_url || 'https://via.placeholder.com/200x200?text=Artist');
  const [firstName, setFirstName] = useState(auth.user?.firstname || '');
  const [lastName, setLastName] = useState(auth.user?.lastname || '');
  // Manage genres select state separately from react-hook-form
  const [selectedGenres, setSelectedGenres] = useState(() => {
    if (artistData?.genres && Array.isArray(artistData.genres)) {
      return artistData.genres.map((genre) => ({ value: genre, label: genre }));
    }
    return [];
  });

  // Other States
  const [smsConsent, setSmsConsent] = useState(auth.user?.isSMSenabled || false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);

  // Email/OTP
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  // Toggle show/hide email form
  const [isEmailFormVisible, setIsEmailFormVisible] = useState(false);

  // Password
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [pLoading, setPLoading] = useState(false);
  // Toggle show/hide password form
  const [isPasswordFormVisible, setIsPasswordFormVisible] = useState(false);

  // Stripe - memoize to prevent re-initialization
  const stripePromise = useMemo(() => 
    loadStripe('pk_test_51PLQgX2KnKdf1YCEVojH8ER1TVelnSOzUmuNJIJaOadbq1EatVpO2RVndyWTAX0CUmp7fomagqJFkkCQpOJuJB9O00kq1zQolv'),
    []
  );

  // Genre options (using your provided list)
  const genres = [
    'Afrobeats',
    'AfroGospel',
    'AfroRave',
    'AfroFusion',
    'EmoAfrobeats',
    'Street-Hop',
    'Pon Pon Music',
    'AfroLife',
    'Highlife',
    'Kuduro',
    'Afrocongo',
    'Gqom',
    'Azonto',
    'Rock',
    'Hard Rock',
    'Soft Rock',
    'Progressive Rock',
    'Punk Rock',
    'Alternative Rock',
    'Jazz',
    'Bebop',
    'Swing',
    'Fusion',
    'Free Jazz',
    'Smooth Jazz',
    'Classical',
    'Baroque',
    'Romantic',
    'Modern',
    'Classical Period',
    'Contemporary Classical',
    'Hip Hop',
    'Rap',
    'Trap',
    'Gangsta Rap',
    'Gospel',
    'Conscious Hip Hop',
    'East Coast Hip Hop',
    'West Coast Hip Hop',
    'Electronic',
    'House',
    'Techno',
    'Dubstep',
    'Trance',
    'Drum and Bass',
    'Ambient',
    'Pop',
    'Teen Pop',
    'Dance Pop',
    'Synth-pop',
    'Electropop',
    'Pop Rock',
    'R&B',
    'Contemporary R&B',
    'Soul',
    'Funk',
    'Disco',
    'New Jack Swing',
    'Country',
    'Bluegrass',
    'Honky Tonk',
    'Outlaw Country',
    'Country Pop',
    'Alternative Country',
    'Reggae',
    'Roots Reggae',
    'Dub',
    'Dancehall',
    'Ska',
    'Rocksteady'
  ].map((genre) => ({ value: genre, label: genre }));

  /* =================
      HANDLERS
  ==================*/
  // Handler for updating profile image (separate method)
  const handleImageSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) {
      toast({
        title: 'No image selected.',
        status: 'error',
        isClosable: true,
      });
      return;
    }
    setIsLoadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      // Replace with your actual API endpoint for updating image
      await axiosInstance.post('/users/update/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({
        title: 'Profile image updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating profile image.',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingImage(false);
    }
  };

  // Handler for updating genres (separate method)
  const handleGenresSubmit = async (e) => {
    e.preventDefault();
    setIsLoadingGenres(true);
    try {
      const genresArray = selectedGenres.map(option => option.value);
      // Replace with your actual API endpoint for updating genres
      await axiosInstance.put('/users/update/genres', { genres: genresArray });
      toast({
        title: 'Genres updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating genres.',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingGenres(false);
    }
  };

  // Handler for updating names
  const handleNamesSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoadingNames(true);
      
      await axiosInstance.put('/users/update/user/names', {
        firstname: firstName,
        lastname: lastName,
      });

      toast({
        title: 'Names updated successfully. Artist verification status reset due to name change.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating names',
        description: error?.response?.data?.detail || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingNames(false);
    }
  };

  // Image preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  // Email change with OTP
  const sendOtp = async () => {
    setOtpLoading(true);
    try {
      const response = await axiosInstance.get('users/profile/send_otp');
      if (response.data) {
        setOtpSent(true);
        toast({
          title: 'OTP sent successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error sending OTP',
        description: error.response?.data?.detail,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleChangeEmail = async (event) => {
    event.preventDefault();
    if (!newEmail || !otp) return;
    try {
      setOtpLoading(true);
      const response = await axiosInstance.post('users/profile/update_email/', {
        new_email: newEmail,
        otp,
      });
      toast({
        title: response.data,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error changing email',
        description: error.response?.data?.detail,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setOtpSent(false);
      setOtp('');
      setOtpLoading(false);
      setNewEmail('');
      // Optionally refresh user data or log out if the email changed
      // auth.logout();
      // navigate('/');
    }
  };

  // Password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast({
        title: 'Passwords do not match',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      setPLoading(true);
      await axiosInstance.post('users/profile/reset/password/', {
        old_password: oldPassword,
        new_password: newPassword,
      });
      toast({
        title: 'Password reset successful',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error resetting password',
        description: error.response?.data?.detail,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setPLoading(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      // Log out after changing password
      auth.logout();
      navigate('/');
    }
  };

  // SMS consent
  const handleSmsConsentChange = async () => {
    try {
      const newSmsConsent = !smsConsent;
      await axiosInstance.put(`/users/update/sms/option?update=${newSmsConsent}`);
      setSmsConsent(newSmsConsent);
      toast({
        title: 'SMS consent updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating SMS consent',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // MFA (QR Code)
  const handleEnableAuthenticator = async () => {
    try {
      const response = await axiosInstance.get('/auth/qr-code');
      setQrCodeUrl(response.data.data);
      toast({
        title: 'Authenticator QR code generated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error generating QR code',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box py={8} px={4} bg="dark.100">
      <Flex direction="column" alignItems="center">
        <Heading color="dark.900" mb={10}>
          Settings
        </Heading>

        {/* ================================
            1) PROFILE (Genres & Image)
        =================================*/}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Profile
          </Text>
          
          {/* Form for updating names */}
          <Box mb={8}>
            <form onSubmit={handleNamesSubmit}>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="primary.500">First Name</FormLabel>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                    variant="filled"
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel color="primary.500">Last Name</FormLabel>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last Name"
                    variant="filled"
                  />
                </FormControl>
                
                <Button type="submit" colorScheme="teal" isLoading={isLoadingNames}>
                  Update Names
                </Button>
              </Stack>
            </form>
          </Box>
          
          {/* Separate form for updating profile image */}
          <Box mb={8}>
            <form onSubmit={handleImageSubmit}>
              <FormControl mb={4}>
                <FormLabel color="primary.500">Profile Image</FormLabel>
                <Image
                  src={imagePreview}
                  alt="Artist Image"
                  boxSize="200px"
                  objectFit="cover"
                  mb={4}
                />
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  color="primary.900"
                  variant="unstyled"
                />
              </FormControl>
              <Button type="submit" colorScheme="teal" isLoading={isLoadingImage}>
                Update Image
              </Button>
            </form>
          </Box>

          {/* Separate form for updating genres */}
          <Box mb={8}>
            <form onSubmit={handleGenresSubmit}>
              <FormControl>
                <FormLabel color="primary.500">Genres</FormLabel>
                <Controller
                  name="genres"
                  control={control}
                  defaultValue={selectedGenres}
                  render={({ field }) => (
                    <ChakraSelect
                      {...field}
                      value={selectedGenres}
                      onChange={(value) => {
                        field.onChange(value);
                        setSelectedGenres(value);
                      }}
                      components={{
                        DropdownIndicator: (props) => (
                          <components.DropdownIndicator {...props}>
                            <ChevronDownIcon color="primary.500" />
                          </components.DropdownIndicator>
                        ),
                      }}
                      options={genres}
                      isMulti
                      closeMenuOnSelect={false}
                    />
                  )}
                />
              </FormControl>
              <Button type="submit" colorScheme="teal" isLoading={isLoadingGenres} mt={4}>
                Update Genres
              </Button>
            </form>
          </Box>
        </Box>

        <Divider mb={8} />

        {/* ===================================
            2) CURRENT EMAIL / CHANGE EMAIL
        =====================================*/}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={2} color="dark.900">
            Email
          </Text>
          <Text mb={4} color="primary.900">
            {auth.user.email}
          </Text>
          <Button
            colorScheme="teal"
            onClick={() => setIsEmailFormVisible(!isEmailFormVisible)}
          >
            {isEmailFormVisible ? 'Cancel' : 'Change Email'}
          </Button>

          {isEmailFormVisible && (
            <Box mt={4}>
              {!otpSent ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendOtp();
                  }}
                >
                  <FormControl isRequired>
                    <FormLabel color="primary.900">New Email</FormLabel>
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    colorScheme="green"
                    mt={4}
                    w="200px"
                    isLoading={otpLoading}
                  >
                    Send OTP
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleChangeEmail}>
                  <FormControl isRequired>
                    <FormLabel color="primary.900">Enter OTP</FormLabel>
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    colorScheme="green"
                    mt={4}
                    w="200px"
                    isLoading={otpLoading}
                  >
                    Change Email
                  </Button>
                </form>
              )}
            </Box>
          )}
        </Box>

        <Divider mb={8} />

        {/* =================================
            3) CHANGE PASSWORD
        ===================================*/}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Password
          </Text>
          <Button
            onClick={() => setIsPasswordFormVisible(!isPasswordFormVisible)}
          >
            {isPasswordFormVisible ? 'Cancel' : 'Change Password'}
          </Button>

          {isPasswordFormVisible && (
            <VStack spacing={4} align="flex-start" w="100%" mt={4}>
              <form onSubmit={handleResetPassword} style={{ width: '100%' }}>
                <FormControl isRequired>
                  <FormLabel color="primary.900">Old Password</FormLabel>
                  <Input
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired mt={4}>
                  <FormLabel color="primary.900">New Password</FormLabel>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired mt={4}>
                  <FormLabel color="primary.900">Confirm New Password</FormLabel>
                  <Input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="green"
                  mt={4}
                  w="full"
                  isLoading={pLoading}
                >
                  Change Password
                </Button>
              </form>
            </VStack>
          )}
        </Box>

        <Divider mb={8} />

        {/* =================================================
            4) SUBSCRIPTION, PAYMENT METHODS, & OTHER PARTS
        ==================================================*/}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Subscription & Payment Methods
          </Text>
          <Elements stripe={stripePromise}>
            <Settings />
          </Elements>
        </Box>

        <Divider mb={8} />

        {/* Example of 'other' settings: SMS + MFA */}
        <Box w="100%" maxW="800px" mb={10}>
          <Text fontSize="2xl" fontWeight="bold" mb={4} color="dark.900">
            Additional Security
          </Text>

          {/* SMS Consent */}
          <FormControl display="flex" alignItems="center" mb={6}>
            <FormLabel mb="0" color="primary.500">
              Opt-in for SMS Notifications?
            </FormLabel>
            <Switch
              isChecked={smsConsent}
              onChange={handleSmsConsentChange}
              colorScheme="teal"
            />
          </FormControl>

          {/* MFA / Authenticator */}
          <Button onClick={handleEnableAuthenticator} colorScheme="teal">
            View MFA QR Code
          </Button>
          {qrCodeUrl && (
            <Box mt={4}>
              <QRCodeCanvas value={qrCodeUrl} size={200} />
            </Box>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default ArtistProfile;